import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { Button, Card, CardHeader, Col, Progress, Row, Spinner, Table } from 'reactstrap'
import { api } from '../../utils'
import SetTitle from '../../utils/set_title'
import VolumeRetrieveHistoricalRecovery from './VolumeRetrieveHistoricalRecovery'

const ITEM_PER_PAGE = 10
const IN_PROGRESS_STATUSES = new Set(['PLANNED', 'RETRIEVING_DATA', 'IMPORTING', 'EXPORTING'])

function sortExecutions(executions) {
  return [...executions].sort((left, right) => {
    const leftRunDate = left.run_date || left.date || ''
    const rightRunDate = right.run_date || right.date || ''

    if (leftRunDate !== rightRunDate) {
      return rightRunDate.localeCompare(leftRunDate)
    }

    return right.id - left.id
  })
}

function buildExecutionRows(executions, stats) {
  const orderedExecutions = sortExecutions(executions)

  return orderedExecutions.map((execution, index) => {
    const totalKeywords = stats.totalKeywords || 0
    const shouldShowLiveProgress = index === 0 && IN_PROGRESS_STATUSES.has(execution.status)

    return {
      ...execution,
      stats: {
        totalKeywords,
        filledKeywords: shouldShowLiveProgress ? stats.filledKeywords : totalKeywords,
      },
    }
  })
}

function VolumeRetrieveExecutions() {
  SetTitle('Market Screener Executions')

  const { id } = useParams()
  const history = useHistory()
  const [schedule, setSchedule] = useState(null)
  const [volumeReports, setVolumeReports] = useState([])
  const [pages, setPages] = useState(0)
  const [actualPage, setActualPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function load() {
      setLoading(true)
      setError(null)

      try {
        const [scheduleResponse, reportsResponse, keywordStatsResponse] = await Promise.all([
          api.get(`/market-screener-detail/${id}`),
          api.get('volume-report/', {
            params: {
              id,
              page_size: ITEM_PER_PAGE,
              page: actualPage,
            },
          }),
          api.get('keyword-volume/', {
            params: { volume_report_scheduled_id: id },
          }),
        ])

        if (!isMounted) {
          return
        }

        const keywordStats = {
          totalKeywords: keywordStatsResponse.data.total,
          filledKeywords: keywordStatsResponse.data.filled,
        }

        setSchedule(scheduleResponse.data)
        setVolumeReports(buildExecutionRows(reportsResponse.data.results, keywordStats))
        setPages(Math.ceil(reportsResponse.data.count / ITEM_PER_PAGE))
      } catch (loadError) {
        if (isMounted) {
          setError('Error fetching market screener executions.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [actualPage, id])

  const handlePageClick = (data) => {
    setActualPage(data.selected + 1)
  }

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <div className="text-center text-danger">{error}</div>
  }

  return (
    <>
      <VolumeRetrieveHistoricalRecovery
        isOpen={isRecoveryOpen}
        onClose={() => setIsRecoveryOpen(false)}
        scheduleId={id}
        initialDetails={schedule}
      />
      <Row style={{ marginBottom: '100px' }}>
        <div className="col pb-5">
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row style={{ marginLeft: '0px' }}>
                <Col md={8}>
                  <h1 className="mb-0">Market Screener Executions</h1>
                </Col>
                <Col md={4}>
                  <div
                    className="d-flex justify-content-end align-items-center"
                    style={{ gap: '8px', flexWrap: 'wrap' }}
                  >
                    <Button color="info" type="button" onClick={() => setIsRecoveryOpen(true)}>
                      Historical Recovery
                    </Button>
                    <Button color="warning" type="button" onClick={() => history.push('/market-screener')}>
                      &lt;- Go Back
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '80px' }}>
                    ID
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '220px' }}>
                    Name
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '120px' }}>
                    Status
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '130px' }}>
                    Run Date
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '220px' }}>
                    Period Downloaded
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '160px' }}>
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody>
                {volumeReports.map((item) => {
                  const { totalKeywords, filledKeywords } = item.stats
                  const barClass = filledKeywords === totalKeywords ? 'bg-success' : 'bg-warning'

                  return (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{item.status}</td>
                      <td>{item.run_date || item.date}</td>
                      <td>{`${item.period_downloaded_start} : ${item.period_downloaded_end}`}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div style={{ width: '120px' }}>
                            <Progress max={totalKeywords} value={filledKeywords} barClassName={barClass} />
                          </div>
                          <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                            {filledKeywords}/{totalKeywords}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {pages > 0 ? (
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            ) : null}
          </Card>
        </div>
      </Row>
    </>
  )
}

export default VolumeRetrieveExecutions
